import { Account, AccountType } from '@ndvr/portfolio-api';

import { formatCurrency } from 'utils';
import { getEffectiveTaxBudget } from 'views/account/tax-budget';

export type PolicySummaryValue = { text: string; active: boolean };

export const summarizePolicyToggle = (values: boolean[]): PolicySummaryValue => {
    if (values.length === 0) return { text: '—', active: false };
    if (values.every(Boolean)) return { text: 'On', active: true };
    if (values.every((value) => !value)) return { text: 'Off', active: false };
    return { text: 'Mixed', active: true };
};

/** Tax loss harvesting is an account-level flag (always off for non-taxable account types). */
export const summarizePolicyTaxLossHarvesting = (accounts: Account[]): PolicySummaryValue =>
    summarizePolicyToggle(accounts.map((account) => account.taxLossHarvesting));

export const summarizePolicyTaxBudget = (accounts: Account[], currentYear = new Date().getFullYear()) => {
    const taxableAccounts = accounts.filter((account) => account.accountType === AccountType.TAXABLE);
    if (taxableAccounts.length === 0) return { kind: 'notApplicable' as const };
    const budgets = taxableAccounts.map((account) => getEffectiveTaxBudget(account, currentYear));
    if (budgets.some((budget) => budget.kind === 'unlimited')) {
        return {
            kind: 'unlimited' as const,
            inconsistent: budgets.some((budget) => budget.kind === 'amount'),
        };
    }
    if (budgets.every((budget) => budget.kind === 'none')) return { kind: 'none' as const };
    if (budgets.some((budget) => budget.kind === 'none')) return { kind: 'mixed' as const };
    return {
        kind: 'amount' as const,
        amount: budgets.reduce((sum, budget) => sum + (budget.kind === 'amount' ? budget.amount : 0), 0),
    };
};

export const describePolicyTaxBudget = (
    budget: ReturnType<typeof summarizePolicyTaxBudget>,
    coordinated: boolean,
): PolicySummaryValue => {
    switch (budget.kind) {
        case 'amount':
            return {
                text: `${formatCurrency(budget.amount)} ${coordinated ? 'across accounts' : 'limit'}`,
                active: true,
            };
        case 'unlimited':
            return { text: coordinated ? 'Unlimited across accounts' : 'Unlimited', active: true };
        case 'mixed':
            return { text: 'Partially configured', active: true };
        case 'notApplicable':
            return { text: 'Not applicable', active: false };
        case 'none':
            return { text: 'Not configured', active: false };
    }
};
